import { css } from '@emotion/react'

import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { AboutTeamPerson } from 'components/content/about/aboutTeam/AboutTeamPerson'
import { FullStoryContent } from 'components/content/about/aboutTeam/FullStory'
import {
  SAboutTeam,
  SRikardImage,
  SRikardSection,
  SRikardText,
  STeamGridFooterText,
} from 'components/content/about/aboutTeam/styled'
import { TeamGrid } from 'components/content/about/aboutTeam/TeamGrid'
import { ContentfulImage } from 'components/Image'
import { PrimaryLink } from 'components/Link'
import { useModal } from 'components/modal/Modal'
import { Spacer } from 'components/spacer/Spacer'
import { TranslatableLink } from 'components/TranslatableLink'
import { Caption, Text, Title, Title_h2_default, Title_h3_default } from 'components/Typography'
import { Trans, useTranslation } from 'next-i18next'
import React from 'react'
import { IAboutUsPageFields } from 'types/generated/contentful'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { useWindowSize } from 'utils/hooks'
import { BREAKPOINTS, pxToRem } from 'utils/styled'

export function AboutTeam({
  className,
  content,
}: {
  className?: string
  content: IAboutUsPageFields
}) {
  const { t } = useTranslation('security')
  const images = useContentfulImages()
  const [fullStoryModal, showFullStoryModal] = useModal(
    ({ close }) => <FullStoryContent closeModal={close} />,
    {
      modalCss: { maxWidth: `${pxToRem(766)}rem` },
    }
  )

  const { isDesktop } = useWindowSize()

  return (
    <>
      {fullStoryModal}
      <Block
        className={className}
        css={css`
          padding: 6rem 0;

          @media ${BREAKPOINTS.MD.max} {
            flex-direction: column;
            padding: 2rem 0 3rem;
          }
        `}
      >
        <SAboutTeam>
          <div>
            <Title variant={Title_h3_default} as="h3" css={{ maxWidth: `${pxToRem(636)}rem` }}>
              {t('security.about.team_title')}
            </Title>

            <Spacer size={[1.5, 4]} />

            <SRikardSection>
              <SRikardImage>
                <ContentfulImage css={{ maxWidth: '100%' }} {...images.about_us_founder} />
              </SRikardImage>

              <SRikardText>
                <Caption css={{ color: 'var(--c-blue500)', marginBottom: '1rem' }}>
                  {t('security.about.ceo_caption')}
                </Caption>

                <Title variant={Title_h2_default} as="h2" css={{ marginBottom: '1.5rem' }}>
                  {t('security.about.ceo_title')}
                </Title>

                <Text>
                  {t('security.about.ceo_desc')}
                  <br />
                  <br />
                  <span css={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                    "{t('security.about.ceo_quote')}"
                  </span>
                  <Button
                    type="button"
                    size="large"
                    css={css`
                      margin-top: 2rem;
                      @media ${BREAKPOINTS.MD.max} {
                        width: 100%;
                      }
                    `}
                    onClick={showFullStoryModal}
                  >
                    {t('security.about.ceo_button')}
                  </Button>
                </Text>
              </SRikardText>
            </SRikardSection>
          </div>

          <div css={{ marginBottom: `7rem` }}>
            <Title variant={Title_h2_default} as="h2" css={{ maxWidth: `${pxToRem(483)}rem` }}>
              {t('security.about.team_members_title')}
            </Title>

            <Spacer size={[1.5, 4]} />

            <TeamGrid
              footer={
                <STeamGridFooterText>
                  <Trans
                    t={t}
                    i18nKey={
                      isDesktop
                        ? 'security.about.team_members_footer'
                        : 'security.about.team_members_footer_mobile'
                    }
                    components={[
                      <PrimaryLink
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.cleevio.com/"
                        key={0}
                      />,
                    ]}
                  />
                </STeamGridFooterText>
              }
            >
              {content.teamMembers?.map((item, i) => {
                if (!item) return null
                return (
                  <AboutTeamPerson
                    key={i}
                    position={
                      // we allow the title to contain a special character combination '\n', that
                      // transforms into a new line on desktop and ', ' on mobile
                      // (this is done to keep the behaviour of Daniel Sieberg's title, which shows on two lines on desktop and one line on mobile devices)
                      item.fields.title?.replace(/\\n/g, isDesktop ? '\n' : ', ') ?? undefined
                    }
                    name={item.fields.name ?? undefined}
                    singlePhotoSrc={item.fields.photo?.fields.file.url ?? undefined}
                  />
                )
              })}
            </TeamGrid>
          </div>

          <div>
            <Title variant={Title_h3_default} as="h3">
              {t('security.about.investors_title')}
            </Title>

            <Text css={{ margin: `1rem 0`, maxWidth: '50rem' }}>
              <Trans
                t={t}
                i18nKey={'security.about.investors_desc'}
                components={{
                  moxxie: <TranslatableLink variant="primary" href="https://www.moxxie.vc/" />,
                  khosla: (
                    <TranslatableLink variant="primary" href="https://www.khoslaventures.com/" />
                  ),
                  rose: (
                    <TranslatableLink variant="primary" href="https://concreterosecapital.com/" />
                  ),
                  bling: <TranslatableLink variant="primary" href="https://www.blingcap.com/" />,
                  synetro: <TranslatableLink variant="primary" href="https://www.synetro.com/" />,
                  azure: <TranslatableLink variant="primary" href="http://www.azurecap.com/" />,
                  alumni: <TranslatableLink variant="primary" href="https://www.av.vc/" />,
                  primer: (
                    <TranslatableLink variant="primary" href="https://www.primerholdings.com/" />
                  ),
                  gaingels: <TranslatableLink variant="primary" href="https://gaingels.com/" />,
                  lane: <TranslatableLink variant="primary" href="https://lanevc.com/" />,
                }}
              />
            </Text>

            <Spacer size={[0.5, 2]} />

            <TeamGrid>
              {content.investorsPeople?.map((item, i) => {
                if (!item) return null
                return (
                  <AboutTeamPerson
                    key={i}
                    position={item.fields.title ?? undefined}
                    name={item.fields.name ?? undefined}
                    singlePhotoSrc={item.fields.photo?.fields.file.url ?? undefined}
                  />
                )
              })}
            </TeamGrid>
          </div>
        </SAboutTeam>
      </Block>
    </>
  )
}
